import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Copy, Share2, ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  category: string;
  featured_image_url: string | null;
}

interface SocialPost {
  platform: string;
  text: string;
  variant: string;
}

export default function SocialMediaManager() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const { data, error } = await supabase
        .from("articles")
        .select("id, title, slug, excerpt, category, featured_image_url")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error("Error loading articles:", error);
      toast({
        title: "Error",
        description: "Failed to load articles",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSocialPosts = (article: Article): SocialPost[] => {
    const articleUrl = `${window.location.origin}/article/${article.slug}`;
    
    return [
      {
        platform: "Twitter/X",
        variant: "Short & Punchy",
        text: `${article.title}\n\nRead the full story: ${articleUrl}`,
      },
      {
        platform: "Twitter/X",
        variant: "With Emoji",
        text: `🔥 ${article.title}\n\n✨ ${article.excerpt || "Essential menopause information you need to know."}\n\n👉 ${articleUrl}`,
      },
      {
        platform: "Facebook",
        variant: "Detailed",
        text: `${article.title}\n\n${article.excerpt || "Discover essential insights about menopause health and wellness."}\n\nRead the full article here: ${articleUrl}\n\n#Menopause #WomensHealth #Wellness`,
      },
      {
        platform: "LinkedIn",
        variant: "Professional",
        text: `${article.title}\n\n${article.excerpt || "Important health information for women navigating menopause."}\n\nLearn more: ${articleUrl}\n\n#WomensHealth #Menopause #HealthEducation`,
      },
      {
        platform: "Instagram",
        variant: "Caption",
        text: `${article.title}\n\n${article.excerpt || "Swipe to learn more about menopause health."}\n\n🔗 Link in bio: ${articleUrl}\n\n#Menopause #WomensHealth #HealthTips #Wellness #MenopauseSupport`,
      },
    ];
  };

  const copyToClipboard = async (text: string, platform: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${platform} post copied to clipboard`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <Share2 className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">Social Media Manager</h1>
          </div>
          <p className="text-muted-foreground">
            Pre-written posts for all your articles. Click to copy and share instantly.
          </p>
        </div>

        <div className="space-y-8">
          {articles.map((article) => {
            const posts = generateSocialPosts(article);
            
            return (
              <Card key={article.id} className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <div className="flex items-start gap-4">
                    {article.featured_image_url && (
                      <img
                        src={article.featured_image_url}
                        alt={article.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <CardTitle className="text-xl mb-2">{article.title}</CardTitle>
                          <CardDescription>{article.excerpt}</CardDescription>
                        </div>
                        <Badge variant="secondary">{article.category}</Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-6">
                  <Tabs defaultValue="twitter" className="w-full">
                    <TabsList className="grid w-full grid-cols-4 mb-6">
                      <TabsTrigger value="twitter">Twitter/X</TabsTrigger>
                      <TabsTrigger value="facebook">Facebook</TabsTrigger>
                      <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
                      <TabsTrigger value="instagram">Instagram</TabsTrigger>
                    </TabsList>

                    {["twitter", "facebook", "linkedin", "instagram"].map((platform) => {
                      const platformPosts = posts.filter(
                        (p) => p.platform.toLowerCase().includes(platform)
                      );

                      return (
                        <TabsContent key={platform} value={platform} className="space-y-4">
                          {platformPosts.map((post, idx) => (
                            <div
                              key={idx}
                              className="border rounded-lg p-4 bg-card hover:bg-accent/5 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-4 mb-3">
                                <Badge variant="outline">{post.variant}</Badge>
                                <Button
                                  size="sm"
                                  onClick={() => copyToClipboard(post.text, post.platform)}
                                  className="shrink-0"
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy
                                </Button>
                              </div>
                              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                                {post.text}
                              </p>
                            </div>
                          ))}
                        </TabsContent>
                      );
                    })}
                  </Tabs>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              No articles found. Create some articles to generate social media posts.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
